/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Platform Health Dashboard
 * Feature #141: Platform Self-Monitoring - US1 + US2 + US3
 * Constitution: v1.19.0
 *
 * Conformed to Spec 197 design blueprint — migrated the hand-rolled header,
 * ad-hoc KPI/tab/table/modal markup, and raw gray/blue palette onto the
 * canonical Tailwind+token UI kit (PageLayout / Card / DataTable / StatCard /
 * Button / Input / Select / Badge / Modal / states). Presentation only — the
 * React Query data hooks, name-intelligence derivations, sort/filter/pagination
 * logic, CSV export, and history-modal wiring are unchanged.
 */

import React, { useState, useMemo } from 'react';
import {
  Activity, Server, Database, Zap, RefreshCw, Download,
  CheckCircle2, XCircle, AlertTriangle, Clock, Search, Filter,
} from 'lucide-react';
import clsx from 'clsx';
import {
  usePlatformSummary,
  useContainers,
  useContainerHistory,
  useRegistry,
  useLogicApps,
  useLogicAppHistory,
  usePlatformJobs,
  usePlatformReachability,
  formatBytes,
  formatRelativeTime,
  getContainerStatusColor,
  getContainerStatusLabel,
  type TileSignal,
} from '../../services/operations/platformService';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Badge,
  StatCard,
  EmptyState as KitEmptyState,
  ErrorState,
  LoadingState,
  Modal,
  Extended,
  QuickGlance,
  AppTooltip,
  NotCollected,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui';
import type { BadgeVariant } from '../../components/ui';

// ============================================================================
// Constants
// ============================================================================

const TABS = ['containers', 'jobs', 'registry', 'logic-apps'] as const;
type TabId = (typeof TABS)[number];

const TAB_LABELS: Record<TabId, string> = {
  containers: 'Container Apps/Jobs',
  jobs: 'Job Status',
  // Spec 216 T023: renamed from the ambiguous "Registry"
  registry: 'Container Registry',
  'logic-apps': 'Logic Apps',
};

const TAB_ICONS: Record<TabId, React.FC<{ className?: string }>> = {
  containers: Server,
  jobs: Clock,
  registry: Database,
  'logic-apps': Zap,
};

/** Spec 216: token badge variants for the {ok, attention, stale, not-collected} signals. */
const SIGNAL_VARIANT: Record<TileSignal, BadgeVariant> = {
  ok: 'success',
  attention: 'error',
  stale: 'warning',
  'not-collected': 'secondary',
};

const SIGNAL_LABEL: Record<TileSignal, string> = {
  ok: 'OK',
  attention: 'Attention',
  stale: 'Stale',
  'not-collected': 'Not collected',
};

/** Map the legacy status-color vocabulary onto kit Badge variants. */
const STATUS_VARIANT: Record<'green' | 'red' | 'gray' | 'yellow', BadgeVariant> = {
  green: 'success',
  red: 'error',
  gray: 'secondary',
  yellow: 'warning',
};

// ============================================================================
// Name Intelligence — derive environment, project, and product from naming
// ============================================================================

type EnvTag = 'prod' | 'uat' | 'dev' | 'unknown';

const ENV_VARIANT: Record<EnvTag, BadgeVariant> = {
  prod: 'error',
  uat: 'warning',
  dev: 'info',
  unknown: 'secondary',
};

const ENV_LABELS: Record<EnvTag, string> = {
  prod: 'PROD',
  uat: 'UAT',
  dev: 'DEV',
  unknown: '—',
};

/** Known project patterns — maps name prefix to a human-readable product. */
const PROJECT_MAP: [RegExp, string][] = [
  [/^fabric-(collection|web)/, 'Fabric Control Plane'],
  [/^(rxmgr|cmpdr|orgs|pricing)-ods/, 'Lakehouse ODS'],
  [/^claimsdw/, 'Claims DW'],
  [/^datamave/, 'DataMave'],
  [/^dashpipe/, 'DashPipe'],
  [/^dds-(devhub|web)/, 'DDS Web'],
  [/^ca-(neoai|systemagent)/, 'NeoAI / System'],
];

function deriveEnv(name: string): EnvTag {
  const lc = name.toLowerCase();
  if (lc.includes('-prod')) return 'prod';
  if (lc.includes('-uat')) return 'uat';
  if (lc.includes('-dev')) return 'dev';
  return 'unknown';
}

function deriveProject(name: string): string {
  for (const [pattern, label] of PROJECT_MAP) {
    if (pattern.test(name)) return label;
  }
  return 'Other';
}

/** Derive a short descriptive purpose from the container name. */
function deriveRole(name: string, isJob: boolean): string {
  if (name.includes('-baseline')) return 'Baseline sync';
  if (name.includes('-daily')) return 'Daily sync';
  if (name.includes('-monthly')) return 'Monthly sync';
  if (name.includes('-collection')) return 'Data collection';
  if (name.includes('-ingest')) return 'Data ingestion';
  if (name.includes('-import')) return 'Data import';
  if (name.includes('-decode')) return 'Decoding';
  if (name.includes('-promote')) return 'Environment promotion';
  if (name.includes('-transform')) return 'Data transform';
  if (name.includes('-load')) return 'Data load';
  if (name.includes('-sync')) return 'Data sync';
  if (name.includes('-seed')) return 'Initial seed';
  if (name.includes('-full')) return 'Full pipeline';
  if (name.includes('-web') || name.includes('web-')) return 'Web application';
  if (name.includes('-scheduler')) return 'Scheduler';
  if (name.includes('-dm-dims')) return 'Dimension build';
  if (!isJob) return 'Web application';
  return isJob ? 'Scheduled job' : 'Service';
}

// ============================================================================
// Sub-components: Badges and Pills
// ============================================================================

const EnvBadge: React.FC<{ env: EnvTag }> = ({ env }) => (
  <Badge variant={ENV_VARIANT[env]} isOutlined>
    {ENV_LABELS[env]}
  </Badge>
);

const ProjectBadge: React.FC<{ project: string }> = ({ project }) => (
  <Badge variant="info" isOutlined>
    {project}
  </Badge>
);

const StatusBadge: React.FC<{
  status: 'green' | 'red' | 'gray' | 'yellow';
  label: string;
}> = ({ status, label }) => (
  <Badge variant={STATUS_VARIANT[status]} isDot isPill>
    {label}
  </Badge>
);

const HealthBadge: React.FC<{ isHealthy: boolean; isStale: boolean; issues: string[] }> = ({
  isHealthy,
  isStale,
  issues,
}) => {
  if (isHealthy) {
    return (
      <div className="flex items-center gap-2 text-[var(--color-status-success)]">
        <CheckCircle2 className="h-5 w-5" />
        <span className="font-semibold">All Healthy</span>
      </div>
    );
  }
  const severity = isStale || issues.some((i) => i.includes('Failed')) ? 'critical' : 'warning';
  const Icon = severity === 'critical' ? XCircle : AlertTriangle;
  const color = severity === 'critical' ? 'text-[var(--color-status-danger)]' : 'text-[var(--color-status-warning)]';
  return (
    <div>
      <div className={clsx('flex items-center gap-2', color)}>
        <Icon className="h-5 w-5" />
        <span className="font-semibold">{severity === 'critical' ? 'Issues Detected' : 'Warnings'}</span>
      </div>
      {issues.length > 0 && (
        <ul className="mt-1 list-inside list-disc space-y-0.5 text-xs text-[var(--color-text-secondary)]">
          {issues.map((i, idx) => <li key={idx}>{i}</li>)}
        </ul>
      )}
    </div>
  );
};

const StaleBadge: React.FC<{ isStale: boolean }> = ({ isStale }) => {
  if (!isStale) return null;
  return (
    <Badge variant="warning" isPill leftIcon={<Clock className="h-3 w-3" />}>
      Stale Data (&gt;24h)
    </Badge>
  );
};

// ACR SKU limits in bytes
const SKU_LIMITS: Record<string, number> = {
  Basic: 10 * 1024 * 1024 * 1024,
  Standard: 100 * 1024 * 1024 * 1024,
  Premium: 500 * 1024 * 1024 * 1024,
};

const StorageWarningBadge: React.FC<{ usedBytes: number | null; sku: string | null }> = ({
  usedBytes,
  sku,
}) => {
  if (!usedBytes || !sku) return null;
  const limit = SKU_LIMITS[sku];
  if (!limit) return null;
  const percentage = (usedBytes / limit) * 100;
  if (percentage < 80) return null;
  const severity = percentage >= 95 ? 'critical' : 'warning';
  const Icon = severity === 'critical' ? XCircle : AlertTriangle;
  return (
    <Badge variant={severity === 'critical' ? 'error' : 'warning'} isPill leftIcon={<Icon className="h-3 w-3" />}>
      {percentage.toFixed(0)}% Used
    </Badge>
  );
};

const FailureRateBadge: React.FC<{ succeeded: number | null; failed: number | null }> = ({
  succeeded,
  failed,
}) => {
  const total = (succeeded ?? 0) + (failed ?? 0);
  if (total === 0) return null;
  const failureRate = ((failed ?? 0) / total) * 100;
  if (failureRate <= 5) return null;
  return (
    <Badge variant="error" isPill leftIcon={<AlertTriangle className="h-3 w-3" />}>
      {failureRate.toFixed(1)}% Failed
    </Badge>
  );
};

// ============================================================================
// Sub-components: Empty State
// ============================================================================

const EmptyState: React.FC<{ category: string }> = ({ category }) => (
  <KitEmptyState
    icon={<Database className="h-10 w-10" />}
    title="No data collected"
    message={`Run the collection job to populate ${category} data.`}
  />
);

// ============================================================================
// Sub-components: KPI Cards
// ============================================================================

const KpiCard: React.FC<{
  title: string;
  value: string | number | null;
  icon: React.ReactNode;
  subtext?: string;
  extra?: React.ReactNode;
  alert?: boolean;
  iconColorClass?: string;
}> = ({ title, value, icon, subtext, extra, alert, iconColorClass = 'text-[var(--color-accent)]' }) => (
  <div
    className={clsx(
      'flex items-start gap-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-5 shadow',
      alert && 'ring-2 ring-[var(--color-status-danger)]',
    )}
  >
    <div className={clsx('rounded-lg bg-[var(--color-bg-subtle)] p-3', iconColorClass)}>{icon}</div>
    <div className="min-w-0 flex-1">
      <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">{title}</p>
      <p className="mt-0.5 text-2xl font-bold text-[var(--color-text-primary)]">
        {value == null ? '—' : value}
      </p>
      {subtext && <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{subtext}</p>}
      {extra && <div className="mt-1.5">{extra}</div>}
    </div>
  </div>
);

// ============================================================================
// Spec 216 T023: KPI-tile criterion tooltip — "how is this tile judged and
// when was its data collected" (FR-008).
// ============================================================================

const TileTooltip: React.FC<{
  tiles: Array<{ key: string; signal: TileSignal; criterion: string; lastCollectedAt: string | null }> | undefined;
  tileKey: string;
  children: React.ReactNode;
}> = ({ tiles, tileKey, children }) => {
  const tile = tiles?.find((t) => t.key === tileKey);
  if (!tile) return <>{children}</>;
  const collected = tile.lastCollectedAt
    ? `collected ${formatRelativeTime(tile.lastCollectedAt)}`
    : 'not collected yet';
  return (
    <AppTooltip title={`${tile.criterion} · ${collected}`}>
      <div>{children}</div>
    </AppTooltip>
  );
};

// ============================================================================
// Sub-components: History Modals (kit <Modal>)
// ============================================================================

const ContainerHistoryModal: React.FC<{
  name: string | null;
  onClose: () => void;
}> = ({ name, onClose }) => {
  const { data: history, isLoading } = useContainerHistory(name);
  return (
    <Modal isOpen={!!name} onClose={onClose} title={`Container History: ${name}`} size="lg">
      {isLoading && <p className="text-[var(--color-text-secondary)]">Loading...</p>}
      {!isLoading && (!history || history.length === 0) && (
        <p className="text-[var(--color-text-secondary)]">No history available</p>
      )}
      {!isLoading && history && history.length > 0 && (
        <div className="space-y-3">
          {history.map((snap) => (
            <div
              key={snap.id}
              className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-[var(--color-bg-subtle)] p-3"
            >
              <div className="w-24 text-sm text-[var(--color-text-secondary)]">{snap.snapshot_date}</div>
              <StatusBadge
                status={getContainerStatusColor(
                  snap.provisioning_state,
                  snap.running_status,
                  snap.last_execution_status
                )}
                label={getContainerStatusLabel(
                  snap.provisioning_state,
                  snap.running_status,
                  snap.last_execution_status,
                  snap.resource_type
                )}
              />
              <div className="text-sm text-[var(--color-text-primary)]">
                {snap.image ? snap.image.split('/').pop()?.split('@')[0] : '—'}
              </div>
              {snap.last_execution_at && (
                <div className="text-xs text-[var(--color-text-secondary)]">
                  Last run: {formatRelativeTime(snap.last_execution_at)}
                </div>
              )}
              {/* exposed_not_used: container config / identity metadata */}
              <div className="flex basis-full flex-wrap items-center gap-x-4 gap-y-1 pl-28 text-[11px] text-[var(--color-text-secondary)]">
                {snap.resource_group && (
                  <span title="Resource group"><span className="font-medium">RG:</span> {snap.resource_group}</span>
                )}
                {snap.environment_name && (
                  <span title="Container Apps environment"><span className="font-medium">Env:</span> {snap.environment_name}</span>
                )}
                {snap.trigger_type && (
                  <span title="Trigger type"><span className="font-medium">Trigger:</span> {snap.trigger_type}</span>
                )}
                {snap.revision_mode && (
                  <span title="Revision mode"><span className="font-medium">Revisions:</span> {snap.revision_mode}</span>
                )}
                {snap.min_replicas != null && (
                  <span title="Minimum replicas"><span className="font-medium">Min replicas:</span> {snap.min_replicas}</span>
                )}
                {snap.azure_resource_id && (
                  <span className="max-w-[24rem] truncate" title={snap.azure_resource_id}><span className="font-medium">ARM ID:</span> {snap.azure_resource_id}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

const LogicAppHistoryModal: React.FC<{
  name: string | null;
  onClose: () => void;
}> = ({ name, onClose }) => {
  const { data: history, isLoading } = useLogicAppHistory(name);
  return (
    <Modal isOpen={!!name} onClose={onClose} title={`Logic App History: ${name}`} size="lg">
      {isLoading && <p className="text-[var(--color-text-secondary)]">Loading...</p>}
      {!isLoading && (!history || history.length === 0) && (
        <p className="text-[var(--color-text-secondary)]">No history available</p>
      )}
      {!isLoading && history && history.length > 0 && (
        <div className="space-y-3">
          {history.map((snap) => (
            <div
              key={snap.id}
              className="flex items-center gap-4 rounded-lg bg-[var(--color-bg-subtle)] p-3"
            >
              <div className="w-24 text-sm text-[var(--color-text-secondary)]">{snap.snapshot_date}</div>
              <StatusBadge
                status={snap.workflow_state === 'Enabled' ? 'green' : 'gray'}
                label={snap.workflow_state ?? 'Unknown'}
              />
              <div className="flex gap-4 text-sm">
                {/* Spec 298 FR-006 (rows 70-71): nullable snapshot counts read "—", never 0. */}
                <span className="text-[var(--color-status-success)]">✓ {snap.runs_succeeded ?? '—'}</span>
                <span className="text-[var(--color-status-danger)]">✗ {snap.runs_failed ?? '—'}</span>
              </div>
              {snap.last_run_at && (
                <div className="text-xs text-[var(--color-text-secondary)]">
                  Last: {formatRelativeTime(snap.last_run_at)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

// ============================================================================
// Spec 216 T023: Reachability strip — the collection job's own heartbeat +
// endpoint probes, displayed straight from the DB (never probed live, FR-018).
// ============================================================================

const ReachabilityStrip: React.FC = () => {
  const { data: components, isLoading } = usePlatformReachability();
  if (isLoading || !components || components.length === 0) return null;

  return (
    <Card className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
          Reachability (as collected by the collection job)
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {components.map((c) => (
          <AppTooltip
            key={`${c.source}-${c.component}`}
            title={`${c.criterion}${c.lastCollectedAt ? ` · collected ${formatRelativeTime(c.lastCollectedAt)}` : ''}${c.detail ? ` · ${c.detail}` : ''}`}
          >
            <span>
              <Badge variant={SIGNAL_VARIANT[c.signal]} isDot isPill>
                {c.component}
                {c.latencyMs != null ? ` · ${c.latencyMs}ms` : ''}
                {c.signal !== 'ok' ? ` · ${SIGNAL_LABEL[c.signal]}` : ''}
              </Badge>
            </span>
          </AppTooltip>
        ))}
      </div>
    </Card>
  );
};

// ============================================================================
// Spec 216 T023: Job Status tab — latest per collection job with judgement
// criteria and collection timestamps.
// ============================================================================

const JobStatusTab: React.FC = () => {
  const { data: jobs, isLoading, error } = usePlatformJobs();

  type Job = NonNullable<typeof jobs>[number];
  const columns: DataTableColumn<Job>[] = [
    {
      key: 'job_name',
      header: 'Job',
      width: '24%',
      render: (j) => (
        <span className="font-medium text-[var(--color-text-primary)]">{j.job_name}</span>
      ),
    },
    {
      key: 'signal',
      header: 'Signal',
      width: '12%',
      render: (j) => (
        <AppTooltip title={j.criterion}>
          <span>
            <Badge variant={SIGNAL_VARIANT[j.signal]} isDot isPill>
              {SIGNAL_LABEL[j.signal]}
            </Badge>
          </span>
        </AppTooltip>
      ),
    },
    {
      key: 'last_execution_status',
      header: 'Last Execution',
      width: '14%',
      render: (j) => (
        <span className="text-[var(--color-text-secondary)]">
          {j.last_execution_status ?? '—'}
          {j.last_execution_end ? ` · ${formatRelativeTime(j.last_execution_end)}` : ''}
        </span>
      ),
    },
    {
      key: 'execution_count_24h',
      header: 'Runs 24h',
      width: '9%',
      align: 'right',
      render: (j) => <span className="text-[var(--color-text-secondary)]">{j.execution_count_24h ?? '—'}</span>,
    },
    {
      key: 'failure_count_24h',
      header: 'Failures 24h',
      width: '9%',
      align: 'right',
      render: (j) => (
        <span
          className={
            (j.failure_count_24h ?? 0) > 0
              ? 'font-medium text-[var(--color-status-danger)]'
              : 'text-[var(--color-text-secondary)]'
          }
        >
          {j.failure_count_24h ?? '—'}
        </span>
      ),
    },
    {
      key: 'expected_schedule',
      header: 'Schedule',
      width: '14%',
      render: (j) => <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">{j.expected_schedule ?? '—'}</span>,
    },
    {
      key: 'lastCollectedAt',
      header: 'Status Collected',
      width: '14%',
      render: (j) => (
        <span className="text-[var(--color-text-secondary)]">{formatRelativeTime(j.lastCollectedAt)}</span>
      ),
    },
  ];

  if (isLoading) return <div className="p-4"><LoadingState rows={6} /></div>;
  if (error) return <div className="p-4"><ErrorState title="Failed to load job statuses" message={(error as Error).message} /></div>;
  if (!jobs || jobs.length === 0) {
    return (
      <div className="p-6 text-center">
        <NotCollected reason="No collection-job status snapshots recorded yet — the platform-health collector writes these each daily run." />
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      rows={jobs}
      rowKey={(j) => j.job_name}
      pageSize={25}
      emptyMessage="No job statuses collected"
    />
  );
};

// ============================================================================
// Tab Components
// ============================================================================

const ContainersTab: React.FC<{
  onSelectContainer: (name: string) => void;
}> = ({ onSelectContainer }) => {
  const { data: containers, isLoading, error } = useContainers();
  const [sortField, setSortField] = useState<string>('last_execution_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [envFilter, setEnvFilter] = useState<EnvTag | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'App' | 'Job'>('all');

  const enriched = useMemo(() => {
    if (!containers) return [];
    return containers.map((c) => {
      const isJob = c.resource_type === 'jobs';
      const env = deriveEnv(c.resource_name);
      const project = deriveProject(c.resource_name);
      const role = deriveRole(c.resource_name, isJob);
      return { ...c, isJob, env, project, role, typeLabel: isJob ? 'Job' as const : 'App' as const };
    });
  }, [containers]);

  const filtered = useMemo(() => {
    return enriched.filter((c) => {
      if (searchTerm && !c.resource_name.toLowerCase().includes(searchTerm.toLowerCase())
          && !c.project.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (envFilter !== 'all' && c.env !== envFilter) return false;
      if (typeFilter !== 'all' && c.typeLabel !== typeFilter) return false;
      return true;
    });
  }, [enriched, searchTerm, envFilter, typeFilter]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = (a as any)[sortField];
      const bv = (b as any)[sortField];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const sa = String(av ?? '').toLowerCase();
      const sb = String(bv ?? '').toLowerCase();
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [filtered, sortField, sortDir]);

  // Compute summary counts for the filter hints
  const envCounts = useMemo(() => {
    const counts = { prod: 0, uat: 0, dev: 0, unknown: 0 };
    enriched.forEach((c) => counts[c.env]++);
    return counts;
  }, [enriched]);
  const typeCounts = useMemo(() => {
    const counts = { App: 0, Job: 0 };
    enriched.forEach((c) => counts[c.typeLabel]++);
    return counts;
  }, [enriched]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: string) => (sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const sortableHeader = (label: string, field: string) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="-mx-1 inline-flex items-center px-1 hover:text-[var(--color-accent)]"
    >
      {label}{sortIndicator(field)}
    </button>
  );

  type EnrichedContainer = (typeof enriched)[number];

  // Spec 216 T023: drop zero-value columns — CPU/memory/replicas are only
  // rendered when at least one row actually carries the metric (the owner's
  // "a lot of zero value information" finding).
  const hasCpu = enriched.some((c) => c.cpu_usage_millicores != null);
  const hasMemory = enriched.some((c) => c.memory_usage_mb != null);
  const hasReplicas = enriched.some((c) => !c.isJob && c.current_replicas != null);

  const columns: DataTableColumn<EnrichedContainer>[] = [
    {
      key: 'resource_name',
      header: sortableHeader('Name', 'resource_name'),
      width: '30%',
      render: (c) => (
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-[var(--color-text-primary)]" title={c.resource_name}>
            {c.resource_name}
          </div>
          <div className="mt-0.5 flex items-center gap-1.5">
            <ProjectBadge project={c.project} />
            <span className="text-[10px] text-[var(--color-text-secondary)]">{c.role}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'env',
      header: 'Env',
      width: '8%',
      render: (c) => <EnvBadge env={c.env} />,
    },
    {
      key: 'resource_type',
      header: sortableHeader('Type', 'resource_type'),
      width: '8%',
      render: (c) => (
        <Badge variant={c.isJob ? 'primary' : 'info'} isOutlined>
          {c.typeLabel}
        </Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '14%',
      render: (c) => (
        <StatusBadge
          status={getContainerStatusColor(c.provisioning_state, c.running_status, c.last_execution_status)}
          label={getContainerStatusLabel(c.provisioning_state, c.running_status, c.last_execution_status, c.resource_type)}
        />
      ),
    },
    ...(hasReplicas
      ? [{
          key: 'replicas',
          header: 'Replicas',
          width: '10%',
          render: (c: EnrichedContainer) => (
            <span className="text-[var(--color-text-secondary)]">
              {c.isJob ? '—' : c.current_replicas != null ? `${c.current_replicas}/${c.max_replicas ?? '?'}` : '—'}
            </span>
          ),
        } satisfies DataTableColumn<EnrichedContainer>]
      : []),
    ...(hasCpu
      ? [{
          key: 'cpu_usage_millicores',
          header: sortableHeader('CPU', 'cpu_usage_millicores'),
          width: '8%',
          align: 'right' as const,
          render: (c: EnrichedContainer) => (
            <span className="text-[var(--color-text-secondary)]">
              {c.cpu_usage_millicores != null ? `${(c.cpu_usage_millicores / 1000).toFixed(1)}` : '—'}
            </span>
          ),
        } satisfies DataTableColumn<EnrichedContainer>]
      : []),
    ...(hasMemory
      ? [{
          key: 'memory_usage_mb',
          header: sortableHeader('Memory', 'memory_usage_mb'),
          width: '10%',
          align: 'right' as const,
          render: (c: EnrichedContainer) => (
            <span className="text-[var(--color-text-secondary)]">
              {c.memory_usage_mb != null ? `${c.memory_usage_mb} MB` : '—'}
            </span>
          ),
        } satisfies DataTableColumn<EnrichedContainer>]
      : []),
    {
      key: 'last_execution_at',
      header: sortableHeader('Last Run', 'last_execution_at'),
      width: '12%',
      render: (c) => (
        <span className="text-[var(--color-text-secondary)]">
          {c.isJob ? formatRelativeTime(c.last_execution_at) : 'Always-on'}
        </span>
      ),
    },
  ];

  if (isLoading) return <div className="p-4"><LoadingState rows={6} /></div>;
  if (error) return <div className="p-4"><ErrorState title="Failed to load containers" message={(error as Error).message} /></div>;
  if (!containers || containers.length === 0) return <EmptyState category="container" />;

  return (
    <div>
      {/* Toolbar: search + filters */}
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-4 py-3">
        <div className="min-w-[200px] max-w-xs flex-1">
          <Input
            isFullWidth
            placeholder="Search name or project..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftAddon={<Search className="h-4 w-4" />}
          />
        </div>

        {/* Environment filter pills */}
        <div className="flex items-center gap-1">
          <Filter className="mr-1 h-4 w-4 text-[var(--color-text-secondary)]" />
          {(['all', 'prod', 'uat', 'dev'] as const).map((env) => (
            <Button
              key={env}
              size="xs"
              variant={envFilter === env ? 'primary' : 'ghost'}
              onClick={() => setEnvFilter(env)}
            >
              {env === 'all' ? `All (${enriched.length})` : `${ENV_LABELS[env]} (${envCounts[env]})`}
            </Button>
          ))}
        </div>

        {/* Type filter */}
        <div className="ml-2 flex items-center gap-1">
          {(['all', 'App', 'Job'] as const).map((t) => (
            <Button
              key={t}
              size="xs"
              variant={typeFilter === t ? 'primary' : 'ghost'}
              onClick={() => setTypeFilter(t)}
            >
              {t === 'all' ? 'All Types' : `${t}s (${typeCounts[t]})`}
            </Button>
          ))}
        </div>

        <span className="ml-auto text-xs text-[var(--color-text-secondary)]">
          Showing {sorted.length} of {enriched.length}
        </span>
      </div>

      <DataTable
        columns={columns}
        rows={sorted}
        rowKey={(c) => c.id}
        pageSize={25}
        onRowClick={(c) => onSelectContainer(c.resource_name)}
        emptyMessage="No containers match the current filters"
      />
    </div>
  );
};

const RegistryTab: React.FC = () => {
  const { data: registries, isLoading, error } = useRegistry();
  const [sortField, setSortField] = useState<string>('registry_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sorted = useMemo(() => {
    if (!registries) return [];
    return [...registries].sort((a, b) => {
      const av = (a as any)[sortField];
      const bv = (b as any)[sortField];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const sa = String(av ?? '').toLowerCase();
      const sb = String(bv ?? '').toLowerCase();
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [registries, sortField, sortDir]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: string) => (sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const sortableHeader = (label: string, field: string) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="-mx-1 inline-flex items-center px-1 hover:text-[var(--color-accent)]"
    >
      {label}{sortIndicator(field)}
    </button>
  );

  type Registry = NonNullable<typeof registries>[number];
  const columns: DataTableColumn<Registry>[] = [
    {
      key: 'registry_name',
      header: sortableHeader('Registry', 'registry_name'),
      width: '14%',
      render: (r) => (
        <span className="font-medium text-[var(--color-text-primary)]" title={r.azure_resource_id ?? undefined}>
          {r.registry_name}
        </span>
      ),
    },
    {
      key: 'sku',
      header: sortableHeader('SKU', 'sku'),
      width: '7%',
      render: (r) => <span className="text-[var(--color-text-secondary)]">{r.sku ?? '—'}</span>,
    },
    {
      key: 'repository_count',
      header: sortableHeader('Repos', 'repository_count'),
      width: '6%',
      align: 'right',
      // Spec 298 FR-006 (rows 72-73): nullable registry snapshot counts read "—", as SKU does.
      render: (r) => <span className="text-[var(--color-text-secondary)]">{r.repository_count ?? '—'}</span>,
    },
    {
      key: 'image_count',
      header: sortableHeader('Images', 'image_count'),
      width: '6%',
      align: 'right',
      render: (r) => <span className="text-[var(--color-text-secondary)]">{r.image_count ?? '—'}</span>,
    },
    {
      key: 'storage_used_bytes',
      header: sortableHeader('Storage', 'storage_used_bytes'),
      width: '8%',
      align: 'right',
      render: (r) => <span className="text-[var(--color-text-secondary)]">{formatBytes(r.storage_used_bytes)}</span>,
    },
    {
      key: 'login_server',
      header: sortableHeader('Login Server', 'login_server'),
      width: '15%',
      render: (r) => (
        <span className="font-mono text-[11px] text-[var(--color-text-secondary)]" title={r.login_server ?? undefined}>
          {r.login_server ?? '—'}
        </span>
      ),
    },
    {
      key: 'resource_group',
      header: sortableHeader('Resource Group', 'resource_group'),
      width: '12%',
      render: (r) => <span className="text-[var(--color-text-secondary)]">{r.resource_group ?? '—'}</span>,
    },
    {
      key: 'public_network_access',
      header: 'Public Access',
      width: '8%',
      render: (r) => <span className="text-[var(--color-text-secondary)]">{r.public_network_access ?? '—'}</span>,
    },
    {
      key: 'zone_redundancy',
      header: 'Zone Redundant',
      width: '7%',
      render: (r) =>
        r.zone_redundancy == null
          ? <span className="text-[var(--color-text-secondary)]">—</span>
          : <StatusBadge status={r.zone_redundancy === 'Enabled' ? 'green' : 'gray'} label={r.zone_redundancy} />,
    },
    {
      key: 'admin_user_enabled',
      header: 'Admin User',
      width: '7%',
      render: (r) =>
        r.admin_user_enabled == null
          ? <span className="text-[var(--color-text-secondary)]">—</span>
          : <StatusBadge status={r.admin_user_enabled ? 'yellow' : 'gray'} label={r.admin_user_enabled ? 'Enabled' : 'Disabled'} />,
    },
    {
      key: 'status',
      header: 'Status',
      width: '7%',
      render: (r) => (
        <>
          <StorageWarningBadge usedBytes={r.storage_used_bytes} sku={r.sku} />
          {!r.storage_used_bytes || !r.sku || (r.storage_used_bytes / (SKU_LIMITS[r.sku] || 1)) * 100 < 80 ? (
            <StatusBadge status="green" label="OK" />
          ) : null}
        </>
      ),
    },
  ];

  if (isLoading) return <div className="p-4"><LoadingState rows={6} /></div>;
  if (error) return <div className="p-4"><ErrorState title="Failed to load registries" message={(error as Error).message} /></div>;
  if (!registries || registries.length === 0) return <EmptyState category="registry" />;

  return (
    <DataTable
      columns={columns}
      rows={sorted}
      rowKey={(r) => r.id}
      minWidth="64rem"
      pageSize={25}
      emptyMessage="No registries found"
    />
  );
};

const LogicAppsTab: React.FC<{
  onSelectLogicApp: (name: string) => void;
}> = ({ onSelectLogicApp }) => {
  const { data: logicApps, isLoading, error } = useLogicApps();
  const [sortField, setSortField] = useState<string>('workflow_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sorted = useMemo(() => {
    if (!logicApps) return [];
    return [...logicApps].sort((a, b) => {
      const av = (a as any)[sortField];
      const bv = (b as any)[sortField];
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const sa = String(av ?? '').toLowerCase();
      const sb = String(bv ?? '').toLowerCase();
      return sortDir === 'asc' ? sa.localeCompare(sb) : sb.localeCompare(sa);
    });
  }, [logicApps, sortField, sortDir]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field: string) => (sortField === field ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const sortableHeader = (label: string, field: string) => (
    <button
      type="button"
      onClick={() => handleSort(field)}
      className="-mx-1 inline-flex items-center px-1 hover:text-[var(--color-accent)]"
    >
      {label}{sortIndicator(field)}
    </button>
  );

  type LA = NonNullable<typeof logicApps>[number];
  const columns: DataTableColumn<LA>[] = [
    {
      key: 'workflow_name',
      header: sortableHeader('Name', 'workflow_name'),
      width: '20%',
      render: (la) => (
        <span className="font-medium text-[var(--color-text-primary)]" title={la.azure_resource_id ?? undefined}>
          {la.workflow_name}
        </span>
      ),
    },
    {
      key: 'workflow_state',
      header: 'State',
      width: '10%',
      render: (la) => (
        <StatusBadge
          status={la.workflow_state === 'Enabled' ? 'green' : 'gray'}
          label={la.workflow_state ?? 'Unknown'}
        />
      ),
    },
    {
      key: 'resource_group',
      header: 'Resource Group',
      width: '14%',
      render: (la) => <span className="text-[var(--color-text-secondary)]">{la.resource_group ?? '—'}</span>,
    },
    {
      key: 'trigger_type',
      header: 'Trigger',
      width: '10%',
      render: (la) => <span className="text-[var(--color-text-secondary)]">{la.trigger_type ?? '—'}</span>,
    },
    {
      key: 'has_managed_connectors',
      header: 'Connectors',
      width: '9%',
      render: (la) =>
        la.has_managed_connectors == null ? (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ) : (
          <StatusBadge
            status={la.has_managed_connectors ? 'green' : 'gray'}
            label={la.has_managed_connectors ? 'Yes' : 'No'}
          />
        ),
    },
    {
      key: 'last_run_at',
      header: sortableHeader('Last Trigger', 'last_run_at'),
      width: '11%',
      render: (la) => <span className="text-[var(--color-text-secondary)]">{formatRelativeTime(la.last_run_at)}</span>,
    },
    {
      key: 'runs_started',
      header: sortableHeader('Runs', 'runs_started'),
      width: '6%',
      align: 'right',
      // Spec 298 FR-006 (rows 74-76): nullable logic-app snapshot counts read "—".
      render: (la) => <span className="text-[var(--color-text-secondary)]">{la.runs_started ?? '—'}</span>,
    },
    {
      key: 'runs_succeeded',
      header: sortableHeader('Success', 'runs_succeeded'),
      width: '6%',
      align: 'right',
      render: (la) => <span className="text-[var(--color-status-success)]">{la.runs_succeeded ?? '—'}</span>,
    },
    {
      key: 'runs_failed',
      header: sortableHeader('Failed', 'runs_failed'),
      width: '6%',
      align: 'right',
      render: (la) => <span className="text-[var(--color-status-danger)]">{la.runs_failed ?? '—'}</span>,
    },
    {
      key: 'rate',
      header: 'Rate',
      width: '8%',
      render: (la) => (
        <>
          <FailureRateBadge succeeded={la.runs_succeeded} failed={la.runs_failed} />
          {(() => {
            const total = (la.runs_succeeded ?? 0) + (la.runs_failed ?? 0);
            if (total === 0) return <span className="text-xs text-[var(--color-text-secondary)]">No runs</span>;
            const successRate = ((la.runs_succeeded ?? 0) / total) * 100;
            const failureRate = ((la.runs_failed ?? 0) / total) * 100;
            if (failureRate <= 5) {
              return <span className="text-xs font-medium text-[var(--color-status-success)]">{successRate.toFixed(0)}% OK</span>;
            }
            return null;
          })()}
        </>
      ),
    },
  ];

  if (isLoading) return <div className="p-4"><LoadingState rows={6} /></div>;
  if (error) return <div className="p-4"><ErrorState title="Failed to load logic apps" message={(error as Error).message} /></div>;
  if (!logicApps || logicApps.length === 0) return <EmptyState category="logic app" />;

  return (
    <DataTable
      columns={columns}
      rows={sorted}
      rowKey={(la) => la.id}
      minWidth="64rem"
      pageSize={25}
      onRowClick={(la) => onSelectLogicApp(la.workflow_name)}
      emptyMessage="No logic apps found"
    />
  );
};

// ============================================================================
// Main Component
// ============================================================================

export default function PlatformHealthPage() {
  const [activeTab, setActiveTab] = useState<TabId>('containers');
  const [selectedContainer, setSelectedContainer] = useState<string | null>(null);
  const [selectedLogicApp, setSelectedLogicApp] = useState<string | null>(null);

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = usePlatformSummary();
  const { data: containersData, refetch: refetchContainers } = useContainers();
  const { data: registryData, refetch: refetchRegistry } = useRegistry();
  const { data: logicAppsData, refetch: refetchLogicApps } = useLogicApps();

  const handleRefresh = () => {
    refetchSummary();
    refetchContainers();
    refetchRegistry();
    refetchLogicApps();
  };

  const handleExportCsv = () => {
    const date = new Date().toISOString().split('T')[0];
    let csv = '';
    let filename = '';

    if (activeTab === 'containers' && containersData) {
      filename = `platform-containers-${date}.csv`;
      csv = 'Name,Type,Environment,Resource Group,CA Environment,Trigger Type,Revision Mode,Min Replicas,Status,Replicas,CPU (cores),Memory (MB),Last Execution,Last Execution At\n';
      containersData.forEach((c) => {
        const isJob = c.resource_type === 'jobs';
        const status = getContainerStatusLabel(c.provisioning_state, c.running_status, c.last_execution_status, c.resource_type);
        const replicas = isJob ? '' : `${c.current_replicas ?? ''}/${c.max_replicas ?? ''}`;
        const cpu = c.cpu_usage_millicores != null ? (c.cpu_usage_millicores / 1000).toFixed(1) : '';
        const mem = c.memory_usage_mb ?? '';
        const lastRun = isJob ? (c.last_execution_at ?? '') : 'Always-on';
        csv += `"${c.resource_name}","${isJob ? 'Job' : 'App'}","${deriveEnv(c.resource_name)}","${c.resource_group ?? ''}","${c.environment_name ?? ''}","${c.trigger_type ?? ''}","${c.revision_mode ?? ''}","${c.min_replicas ?? ''}","${status}","${replicas}","${cpu}","${mem}","${c.last_execution_status ?? ''}","${lastRun}"\n`;
      });
    } else if (activeTab === 'registry' && registryData) {
      filename = `platform-registry-${date}.csv`;
      csv = 'Registry,SKU,Repos,Images,Storage (bytes),Login Server,Resource Group,Public Access,Zone Redundant,Admin User Enabled,Azure Resource Id,Snapshot Date\n';
      registryData.forEach((r) => {
        csv += `"${r.registry_name}","${r.sku ?? ''}","${r.repository_count ?? ''}","${r.image_count ?? ''}","${r.storage_used_bytes ?? ''}","${r.login_server ?? ''}","${r.resource_group ?? ''}","${r.public_network_access ?? ''}","${r.zone_redundancy ?? ''}","${r.admin_user_enabled ?? ''}","${r.azure_resource_id ?? ''}","${r.snapshot_date}"\n`;
      });
    } else if (activeTab === 'logic-apps' && logicAppsData) {
      filename = `platform-logic-apps-${date}.csv`;
      csv = 'Name,State,Resource Group,Trigger Type,Managed Connectors,Last Run At,Last Run Status,Runs Started,Succeeded,Failed,Cancelled\n';
      logicAppsData.forEach((la) => {
        csv += `"${la.workflow_name}","${la.workflow_state ?? ''}","${la.resource_group ?? ''}","${la.trigger_type ?? ''}","${la.has_managed_connectors == null ? '' : la.has_managed_connectors ? 'Yes' : 'No'}","${la.last_run_at ?? ''}","${la.last_run_status ?? ''}","${la.runs_started ?? ''}","${la.runs_succeeded ?? ''}","${la.runs_failed ?? ''}","${la.runs_cancelled ?? ''}"\n`;
      });
    }

    if (!csv) return;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PageLayout
      helpId="auto.platform-health"
      title="Platform Health"
      description="Is FabricControlPlane itself running? Self-monitoring of OUR containers, jobs, registry, and Logic Apps — from data the collection job writes (no live probes)."
      icon={<Activity className="h-6 w-6" />}
      actions={
        <>
          {summary?.snapshotDate && (
            <span className="text-xs text-[var(--color-text-secondary)]">
              Last snapshot: {formatRelativeTime(summary.snapshotDate)}
            </span>
          )}
          <Button variant="outline" leftIcon={<RefreshCw className="h-4 w-4" />} onClick={handleRefresh}>
            Refresh
          </Button>
          <Button variant="outline" leftIcon={<Download className="h-4 w-4" />} onClick={handleExportCsv}>
            Export CSV
          </Button>
        </>
      }
    >
      {/* KPI Cards — each carries its judgement criterion + collection time (T022/T023) */}
      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <TileTooltip tiles={summary?.tiles} tileKey="containers">
        <KpiCard
          title="Container Apps / Jobs"
          value={
            summaryLoading
              ? '...'
              : summary
                ? `${summary.totalContainerApps + summary.totalContainerJobs}`
                : null
          }
          subtext={
            summary
              ? `${summary.totalContainerApps} always-on apps · ${summary.totalContainerJobs} scheduled jobs`
              : undefined
          }
          icon={<Server className="h-6 w-6" />}
          iconColorClass="text-[var(--color-accent)]"
          alert={summary != null && summary.failedJobs > 0}
          extra={summary && summary.failedJobs > 0 ? (
            <span className="text-xs font-medium text-[var(--color-status-danger)]">
              {summary.failedJobs} job(s) in failed state
            </span>
          ) : undefined}
        />
        </TileTooltip>
        <TileTooltip tiles={summary?.tiles} tileKey="registry">
        <KpiCard
          title="Container Registry"
          value={summaryLoading ? '...' : summary ? `${summary.totalRegistries}` : null}
          subtext={
            summary
              ? `${summary.totalRepositories} repos · ${summary.totalImages} images · ${formatBytes(summary.totalStorageBytes)}`
              : undefined
          }
          icon={<Database className="h-6 w-6" />}
          iconColorClass="text-[var(--color-status-info)]"
        />
        </TileTooltip>
        <TileTooltip tiles={summary?.tiles} tileKey="logicApps">
        <KpiCard
          title="Logic Apps"
          value={summaryLoading ? '...' : summary ? `${summary.totalLogicApps}` : null}
          subtext={
            summary
              ? `${summary.runsToday} total runs · ${summary.successRate}% success`
              : undefined
          }
          icon={<Zap className="h-6 w-6" />}
          iconColorClass="text-[var(--color-status-success)]"
          alert={summary != null && summary.successRate < 95}
          extra={summary && summary.failedRuns > 0 ? (
            <span className="text-xs font-medium text-[var(--color-status-danger)]">
              {summary.failedRuns} failed run(s)
            </span>
          ) : undefined}
        />
        </TileTooltip>
        <Card>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[var(--color-text-secondary)]">
            Health Status
          </p>
          {summaryLoading ? (
            <p className="text-[var(--color-text-secondary)]">Loading...</p>
          ) : summary ? (
            <div className="space-y-2">
              <HealthBadge
                isHealthy={summary.isHealthy}
                isStale={summary.isStale}
                issues={summary.healthIssues}
              />
              <StaleBadge isStale={summary.isStale} />
            </div>
          ) : (
            <p className="text-[var(--color-text-secondary)]">—</p>
          )}
        </Card>
      </div>

      {/* Spec 216 T023: reachability signals collected by the job (DB-only) */}
      <ReachabilityStrip />

      <QuickGlance>
        <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
          Showing the at-a-glance summary. Open this page on a larger screen for the full details.
        </Card>
      </QuickGlance>

      <Extended>
      {/* Tab Bar — token underline tabs */}
      <div className="mb-4 flex gap-1 border-b border-[var(--color-border)]">
        {TABS.map((tab) => {
          const Icon = TAB_ICONS[tab];
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={clsx(
                '-mb-px flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab
                  ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                  : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]',
              )}
            >
              <Icon className="h-4 w-4" />
              {TAB_LABELS[tab]}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <Card padding="none">
        {activeTab === 'containers' && (
          <ContainersTab onSelectContainer={setSelectedContainer} />
        )}
        {activeTab === 'jobs' && <JobStatusTab />}
        {activeTab === 'registry' && <RegistryTab />}
        {activeTab === 'logic-apps' && (
          <LogicAppsTab onSelectLogicApp={setSelectedLogicApp} />
        )}
      </Card>
      </Extended>

      {/* Modals */}
      <ContainerHistoryModal
        name={selectedContainer}
        onClose={() => setSelectedContainer(null)}
      />
      <LogicAppHistoryModal
        name={selectedLogicApp}
        onClose={() => setSelectedLogicApp(null)}
      />
    </PageLayout>
  );
}
